<template>
  <div>
    <div v-if="isRenFrance">
      <v-menu v-model="menu" :close-on-content-click="false" eager max-width="290">
        <template v-slot:activator="{ on }">
          <v-text-field
            :dense="dense"
            :outlined="outlined"
            class="label-white"
            :class="{ 'center-text-input': centered }"
            v-on="on"
            :label="label"
            :hint="hint"
            persistent-hint
            :value="showDateInDayMonthYearFormat"
            :error-messages="errorMessages"
            :success="success"
            :hide-details="hideDetails && !errorMessages.length"
            v-model="dateString"
            @input="onManualDateInput"
            :data-vv-name="fullPath"
            :data-vv-as="alias"
            v-validate="validatorRule"
            :header-date-format="headerFormat"
            type="tel"
            placeholder="DD/MM/YYYY"
            @keypress="onKeyPressOnlyNumbers"
          />
        </template>
      </v-menu>
    </div>
    <div v-if="!isRenFrance">
      <v-menu v-model="menu"
            :close-on-content-click="false"
            eager max-width="290">
        <template v-slot:activator="{ on }">
          <v-text-field :dense="dense"
                    :outlined="outlined"
                    class="label-white"
                    :class="{'center-text-input': centered}"
                    readonly
                    v-on="on"
                    :label="label"
                    :hint="hint"
                    persistent-hint
                    :value="showDateInDayMonthYearFormat"
                    :error-messages="errorMessages"
                    :success="success"
                    :hide-details="hideDetails && !errorMessages.length"/>
          </template>

          <v-date-picker :locale="getLocalisation"
                   :max="maxString"
                   :min="minString"
                   :no-title="noTitle"
                   :readonly="readonly"
                   :disabled="readonly"
                   :header-date-format="headerFormat"
                   v-model="dateString"
                   @input="setInput"
                   :data-vv-name="fullPath"
                   :data-vv-as="alias"
                   v-validate="validatorRule"/>
          </v-menu>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import _t, { localeDateFromUTC } from '@/helpers/timeHelper';
import FieldMixin from '@/components/Mixins/FieldMixin.vue';
import { ScenarioExt } from '@/classes/scenario/ScenarioExt';

@Component
export default class DateField extends Mixins(FieldMixin) {

  @Prop({ type: Date })
  public declare readonly input: Date | undefined;

  @Prop({ type: Function })
  public readonly headerFormat: ((date: string) => string) | undefined;

  @Prop({ type: Date })
  public readonly min: Date | undefined;

  @Prop({ type: Date })
  public readonly max: Date | undefined;

  @Prop({ type: String })
  public readonly format!: string;

  @Prop({ type: String })
  public readonly hint: string | undefined;

  @Prop({ type: Boolean, default: false })
  public readonly noTitle!: boolean;

  @Prop({ type: Boolean })
  public readonly hideYear: boolean | undefined;

  @Prop({ type: String })
  public readonly validatorRule: string | undefined;

  @Prop({ type: ScenarioExt, required: true }) // TODO this prop should be required in every question component
  public declare readonly scenario: ScenarioExt;

  protected localInput: Date | null = null;
  private dateString: string | null = null;

  private menu: boolean = false;

  // COMPUTED =======================================================
  get dateFormat(): string {
    return this.isRenFrance ? 'DD/MM/YYYY' : (this.format || 'YYYY-MM-DD');
  }

  get isRenFrance(): boolean {
    return !!this.scenario?.isRenFrance;
  }

  get getLocalisation(): string {
    return this.$i18n.locale;
  }

  get displayedDate(): string | null {
    if (this.hideYear && this.localInput) {
      const date = ('0' + this.localInput.getDate()).slice(-2);
      return `${date} ${this.$t(_t.MONTHS_NAMES[this.localInput.getMonth()])}`;
    }
    return this.localInput && _t.formatDate(this.localInput, 'L', this.getLocalisation);
  }

  get showDateInDayMonthYearFormat(): string | null {
    const date = this.displayedDate;
    const lang = this.$i18n.locale;
    if (lang === 'en') {
      if (date !== null) {
        const [month, day, year] = date.split('/');
        if (month && day && year) {
          return `${day}/${month}/${year}`;
        } else {
          return date;
        }
      }
    }
    return date;
  }

  get minString(): string | undefined {
    if (this.min) {
      const minimum = new Date(this.min);
      minimum.setDate(minimum.getDate() + 1);
      return _t.formatDate(minimum, this.dateFormat, this.getLocalisation);
    }
  }

  get maxString(): string | undefined {
    if (this.max) {
      const maximum = new Date(this.max);
      maximum.setDate(maximum.getDate() - 1);
      return _t.formatDate(maximum, this.dateFormat, this.getLocalisation);
    }
  }

  // METHODS =======================================================

  protected onKeyPressOnlyNumbers(event: KeyboardEvent) {
    const allowed = /[0-9/]/;
    if (!allowed.test(event.key)) {
      event.preventDefault();
    }
  }

  protected setInput(value: string | undefined): void {
    this.menu = false;
    if (value === undefined) {
      this.localInput = null;
      this.$emit('setInput', this.localInput);
      return;
    }
    const parsedValue = localeDateFromUTC(value);
    // conserve the input hours
    if (this.input) {
      parsedValue.setHours(this.input.getHours());
    }
    this.localInput = parsedValue;
    const dateString = this.getDateString(this.localInput);
    if (this.dateString !== dateString) {
      this.dateString = dateString;
    }
    this.$emit('setInput', this.localInput);
  }

  protected onManualDateInput(value: string): void {
    this.menu = false;
    this.removeError(this.fieldName);
    // If field is cleared, reset value
    if (!value) {
      this.localInput = null;
      this.dateString = null;
      this.$emit('setInput', null);
      return;
    }
    // Validate and parse DD/MM/YYYY
    const ddmmyyyyRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    if (value === undefined) {
      this.localInput = null;
      this.$emit('setInput', this.localInput);
      return;
    }
    let parsedValue: Date | null = null;
    // If value matches DD/MM/YYYY, convert to yyyy-mm-dd
    const match = value.match(ddmmyyyyRegex);
    if (match) {
      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1;
      const year = parseInt(match[3], 10);
      if ((!Number.isNaN(day) && !Number.isNaN(month) && !Number.isNaN(year) && day > 0 && month >= 0 && year > 0)) {
        parsedValue = new Date(year, month, day);
        if (this.input) {
          parsedValue.setHours(this.input.getHours());
        }
        this.localInput = parsedValue;
        const dateString = this.getDateString(this.localInput);
        if (this.dateString !== dateString) {
          this.dateString = dateString;
        }
        this.$emit('setInput', this.localInput);
      }
    } else {
      // this.addError(this.fieldName, this.$t(`scenario.wizard.invalid_date_format`).toString());
      // Remove all non-digit and non-slash characters
      let cleaned = value.replace(/[^\d/]/g, '');

      // Auto-insert slash after DD
      if (cleaned.length === 2 && !cleaned.includes('/')) {
        cleaned = cleaned + '/';
      }
      // Auto-insert slash after MM (after DD/)
      if (cleaned.length === 5 && cleaned.match(/^\d{2}\/\d{2}$/)) {
        cleaned = cleaned + '/';
      }

      // Use cleaned value for everything below
      this.dateString = cleaned;
      this.localInput = null;
      // this.dateString = value;
      this.$emit('setInput', this.localInput);
    }
  }

  protected getDateString(date: Date | null | undefined): string | null {
    return date ? _t.formatDate(date, this.dateFormat, this.getLocalisation) : null;
  }

  // HOOKS =======================================================
  // @WatchMixinOverride - only redeclare method
  protected onInputChange(): void {
    if (this.getDateString(this.input) === this.dateString) {
      return;
    }
    this.localInput = this.input || null;
    this.dateString = this.getDateString(this.localInput);
  }

  @Watch('validatorRule')
  private validationChanges() {
    this.validateField();
  }
}
</script>

<style>
</style>
